import React, { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import Modal from './ui/Modal';
import Spinner from './ui/Spinner';
import Input, { Select } from './ui/Input';
import EnvBadge from './ui/EnvBadge';
import { adminApi } from '../config/axios';
import { ENVIRONMENT } from '../config/environment';
import { dateFormat } from '../config/utils';
import { formatAmount } from '../utils/money';
import { useGlobal } from '../contexts/GlobalContext';

export const PERIODS = { ONE_MONTH: { months: 1, label: '1 Month' }, SIX_MONTHS: { months: 6, label: '6 Months' }, ONE_YEAR: { months: 12, label: '1 Year' } };

export function addMonths(date, months) {
  const d = new Date(date.getTime());
  const day = d.getDate();
  d.setMonth(d.getMonth() + months);
  if (d.getDate() !== day) d.setDate(0); // 31 Jan + 1 month -> end of Feb, like Carbon
  return d;
}

export { BUSINESS_LABEL, businessLabel } from '../utils/businessType';

/**
 * Add a subscription in three steps, always on the SAME server the bundle talks to:
 *   1. form        — package / period / devices / payment type
 *   2. confirm     — names the ENVIRONMENT, company, owner email, package, months, start/end
 *                    (a grant from the wrong admin tab must be impossible to miss)
 *   3. done        — the company's ACTIVE subscription, re-read from the server
 *                    (GET /companies/:id/show), never the form values
 * Used by the company profile and by the awaiting-activation list.
 */
export default function AddSubscriptionFlow({ companyId, open, onClose, onDone }) {
  const { showToast } = useGlobal();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('form');
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const { register, handleSubmit, reset, watch, setValue, formState: { errors } } = useForm({
    defaultValues: { type: 'gift', period: 'ONE_MONTH', number_of_devices: 1, currency: 'SAR' },
  });
  const subType = watch('type');

  const fetchInfo = useCallback(() => adminApi.get(`/companies/${companyId}/show`).then((r) => r.data?.data), [companyId]);

  useEffect(() => {
    if (!open) return;
    setStep('form'); setDraft(null); setResult(null); reset();
    setLoading(true);
    fetchInfo().then((i) => { setInfo(i); if (i?.default_currency) setValue('currency', i.default_currency); }).catch(() => showToast('error', 'Error', 'Could not load the company.')).finally(() => setLoading(false));
  }, [open, fetchInfo, reset, showToast]);

  const packageTitle = (id) => info?.packages?.find((p) => p.code === id)?.name ?? '—';

  const onReview = (data) => {
    const months = PERIODS[data.period].months;
    const start = new Date();
    setDraft({ ...data, months, start, end: addMonths(start, months), number_of_devices: Number(data.number_of_devices), amount: data.type === 'gift' ? 0 : Number(data.amount || 0), currency: data.type === 'gift' ? null : data.currency });
    setStep('confirm');
  };

  const submit = async () => {
    setBusy(true);
    try {
      await adminApi.post('/companies/add/subscription', {
        company_id: companyId, package_id: draft.package_id, period: draft.period, type: draft.type,
        number_of_devices: draft.number_of_devices, amount: draft.amount, ...(draft.type === 'gift' ? {} : { currency: draft.currency }),
      });
      const fresh = await fetchInfo();           // the truth, from the server
      setResult(fresh);
      setStep('done');
      onDone?.(fresh);
    } catch (err) {
      showToast('error', 'Error', err?.response?.data?.message ?? 'Failed.');
    } finally {
      setBusy(false);
    }
  };

  const wasActive = !!info?.active_subscription; // before this grant (info is the pre-grant read)
  const owner = info?.owner?.email || info?.company?.email || '—';

  const title = step === 'confirm' ? 'Confirm subscription' : step === 'done' ? 'Subscription active' : 'Add Subscription';
  const footer = step === 'form' ? (
    <>
      <button className="btn-secondary" onClick={onClose}>Cancel</button>
      <button className="btn-primary" disabled={loading} onClick={handleSubmit(onReview)}>Review</button>
    </>
  ) : step === 'confirm' ? (
    <>
      <button className="btn-secondary" disabled={busy} onClick={() => setStep('form')}>Back</button>
      <button className="btn-primary" disabled={busy} data-testid="confirm-grant" onClick={submit}>
        {busy ? 'Adding…' : `Confirm on ${ENVIRONMENT.name}`}
      </button>
    </>
  ) : (
    <button className="btn-primary" onClick={onClose}>Close</button>
  );

  return (
    <Modal open={open} onClose={busy ? () => {} : onClose} title={title} footer={footer}>
      {loading ? (
        <div className="flex justify-center py-8"><Spinner /></div>
      ) : step === 'form' ? (
        <form className="space-y-4" onSubmit={(e) => e.preventDefault()}>
          <div className="flex items-center gap-2 text-xs text-text-muted"><EnvBadge /> <span>{info?.company?.name} · {owner}</span></div>
          <Select label="Package" {...register('package_id', { required: 'Required' })} error={errors.package_id?.message}>
            <option value="">Select package…</option>
            {info?.packages?.map((p) => <option key={p.code} value={p.code}>{p.name}</option>)}
          </Select>
          <Select label="Period" {...register('period', { required: 'Required' })} error={errors.period?.message}>
            {Object.entries(PERIODS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </Select>
          <Input label="Number of Devices" type="number" min="1"
            {...register('number_of_devices', { required: 'Required', min: { value: 1, message: 'Min 1' } })}
            error={errors.number_of_devices?.message} />
          <Select label="Payment Type" {...register('type', { required: 'Required' })} error={errors.type?.message}>
            <option value="gift">Gift (free)</option>
            <option value="bank">Bank Transfer (paid)</option>
          </Select>
          {subType === 'bank' && (
            <div className="grid grid-cols-[1fr,8rem] gap-3 items-start">
              <Input label="Paid Amount" type="number" min="0" step="any"
                {...register('amount', { required: 'Required for bank transfer', validate: (v) => Number(v) > 0 || 'Must be greater than 0' })} error={errors.amount?.message} />
              <Select label="Currency" {...register('currency', { required: 'Required' })} error={errors.currency?.message}>
                {(info?.currencies || [{ code: 'SAR' }, { code: 'JOD' }, { code: 'EGP' }]).map((c) => <option key={c.code} value={c.code}>{c.code}</option>)}
              </Select>
            </div>
          )}
        </form>
      ) : step === 'confirm' ? (
        <div className="space-y-4" data-testid="confirm-step">
          <div className="flex items-center gap-3 rounded-lg px-4 py-3"
               style={{ background: ENVIRONMENT.name === 'PROD' ? 'rgba(220,38,38,.12)' : 'rgba(245,158,11,.10)', border: '1px solid rgba(255,255,255,.12)' }}>
            <EnvBadge />
            <p className="text-sm text-text-main">
              This will be written to the <strong>{ENVIRONMENT.name}</strong> platform
              <span className="text-text-muted"> ({ENVIRONMENT.host || 'unknown host'})</span>.
            </p>
          </div>
          <dl className="grid grid-cols-[9rem,1fr] gap-x-4 gap-y-2 text-sm">
            <dt className="text-text-muted">Environment</dt><dd className="font-semibold">{ENVIRONMENT.name}</dd>
            <dt className="text-text-muted">Company</dt><dd className="font-semibold text-left" dir="auto">{info?.company?.name}</dd>
            <dt className="text-text-muted">Owner email</dt><dd dir="ltr" className="text-left">{owner}</dd>
            <dt className="text-text-muted">Package</dt><dd className="font-semibold">{packageTitle(draft.package_id)}</dd>
            <dt className="text-text-muted">Period</dt><dd>{draft.months} month{draft.months > 1 ? 's' : ''} ({PERIODS[draft.period].label})</dd>
            <dt className="text-text-muted">Devices</dt><dd>{draft.number_of_devices}</dd>
            <dt className="text-text-muted">Payment</dt><dd>{draft.type === 'gift' ? 'Gift (free)' : `Bank transfer — ${formatAmount(draft.amount, draft.currency)}`}</dd>
            <dt className="text-text-muted">Starts</dt><dd>{dateFormat(draft.start)}</dd>
            <dt className="text-text-muted">Ends</dt><dd>{dateFormat(draft.end)}</dd>
          </dl>
          <p className="text-xs text-text-muted">Dates are calculated now; the server records the exact time.
            {!wasActive ? ' The company has no active plan, so its owner will receive the “plan activated” email.' : ' The company already has an active plan; it will be replaced and the owner will receive the “plan renewed” email.'} The email states the package details; it is not an invoice.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="done-step">
          <div className="flex items-center gap-2"><EnvBadge /> <span className="text-sm text-text-muted">as read back from the server</span></div>
          {result?.active_subscription ? (
            <dl className="grid grid-cols-[9rem,1fr] gap-x-4 gap-y-2 text-sm">
              <dt className="text-text-muted">Company</dt><dd className="font-semibold text-left" dir="auto">{result.company?.name}</dd>
              <dt className="text-text-muted">Active package</dt><dd className="font-semibold">{result.active_subscription.package_title}</dd>
              <dt className="text-text-muted">Status</dt><dd>{result.active_subscription.status}</dd>
              <dt className="text-text-muted">Starts</dt><dd>{dateFormat(result.active_subscription.start_date)}</dd>
              <dt className="text-text-muted">Ends</dt><dd>{dateFormat(result.active_subscription.end_date)}</dd>
              <dt className="text-text-muted">Recorded</dt>
              <dd data-testid="done-amount">{result.active_subscription.payment_type === 'GIFT' ? 'Gift (free)' : `${formatAmount(result.active_subscription.amount, result.active_subscription.currency)} · ${String(result.active_subscription.payment_type || '').toLowerCase()}`}</dd>
              <><dt className="text-text-muted">Email</dt><dd>{wasActive ? 'Renewal' : 'Activation'} email sent to {result.owner?.email || result.company?.email}</dd></>
            </dl>
          ) : (
            <p className="text-sm text-danger">The server reports NO active subscription for this company. The grant did not take effect — check the environment and try again.</p>
          )}
        </div>
      )}
    </Modal>
  );
}
